import React, { useState, useEffect, useRef } from 'react';
import { chatMessageApi, chatBannedWordApi, chatMuteApi } from '../../utils/chatAdminApi';
import { userApi } from '../../utils/api';
import toast from 'react-hot-toast';
import { Trash2, Ban, Unlock, Plus, Search, MessageSquare, Users, Shield, Clock, User } from 'lucide-react';

interface ChatStats {
  totalMessages: number;
  onlineUsers: number;
  mutedUsers: number;
  bannedWords: number;
}

interface ChatMessage {
  id: number;
  user_id: number;
  username: string;
  nickname: string | null;
  content: string;
  ip_address: string | null;
  created_at: string;
  is_deleted: number;
}

interface BannedWord {
  id: number;
  word: string;
  created_at: string;
}

interface MutedUser {
  id: number;
  user_id: number;
  username: string;
  reason: string | null;
  mute_type: 'temp' | 'permanent';
  expired_at: string | null;
  created_at: string;
}

const ChatAdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'messages' | 'banned-words' | 'mutes'>('messages');
  const [loading, setLoading] = useState(false);

  // 统计数据
  const [stats, setStats] = useState<ChatStats>({
    totalMessages: 0,
    onlineUsers: 0,
    mutedUsers: 0,
    bannedWords: 0
  });

  // 消息相关
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagePage, setMessagePage] = useState(1);
  const [messageTotal, setMessageTotal] = useState(0);
  const [selectedMessages, setSelectedMessages] = useState<number[]>([]);

  // 敏感词相关
  const [bannedWords, setBannedWords] = useState<BannedWord[]>([]);
  const [newBannedWord, setNewBannedWord] = useState('');

  // 禁言相关
  const [mutedUsers, setMutedUsers] = useState<MutedUser[]>([]);
  const [muteForm, setMuteForm] = useState({
    user_id: '',
    username: '',
    nickname: '',
    reason: '',
    mute_type: 'permanent' as 'temp' | 'permanent',
    duration_minutes: 30
  });
  const [showMuteModal, setShowMuteModal] = useState(false);
  const [searchingUser, setSearchingUser] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // 删除确认相关
  const [messageToDelete, setMessageToDelete] = useState<number | null>(null);

  // 加载统计数据
  const loadStats = async () => {
    try {
      const response = await chatMessageApi.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('加载统计数据失败:', error);
    }
  };

  // 加载消息列表
  const loadMessages = async () => {
    setLoading(true);
    try {
      const response = await chatMessageApi.getMessages(messagePage, 50);
      if (response.success) {
        setMessages(response.data);
        setMessageTotal(response.pagination.total);
      }
    } catch (error) {
      toast.error('加载消息列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载敏感词列表
  const loadBannedWords = async () => {
    try {
      const response = await chatBannedWordApi.getBannedWords();
      if (response.success) {
        setBannedWords(response.data);
      }
    } catch (error) {
      toast.error('加载敏感词列表失败');
    }
  };

  // 加载禁言列表
  const loadMutedUsers = async () => {
    try {
      const response = await chatMuteApi.getMutedUsers();
      if (response.success) {
        setMutedUsers(response.data);
      }
    } catch (error) {
      toast.error('加载禁言列表失败');
    }
  };

  useEffect(() => {
    loadStats();
    loadMessages();
  }, [messagePage]);

  // 删除消息
  const handleDeleteMessage = (id: number) => {
    setMessageToDelete(id);
  };

  const confirmDeleteMessage = async () => {
    if (!messageToDelete) return;
    try {
      const response = await chatMessageApi.deleteMessage(messageToDelete);
      if (response.success) {
        toast.success('删除成功');
        setMessages(prev => prev.filter(msg => msg.id !== messageToDelete));
        loadStats();
      } else {
        toast.error(response.message || '删除失败');
      }
    } catch (error) {
      toast.error('删除失败');
    } finally {
      setMessageToDelete(null);
    }
  };

  const cancelDeleteMessage = () => {
    setMessageToDelete(null);
  };

  // 删除确认相关
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);

  // 批量删除消息
  const handleBatchDelete = () => {
    if (selectedMessages.length === 0) {
      toast.error('请选择要删除的消息');
      return;
    }
    setShowBatchDeleteModal(true);
  };

  const confirmBatchDeleteMessage = async () => {
    try {
      const response = await chatMessageApi.batchDeleteMessages(selectedMessages);
      if (response.success) {
        toast.success('批量删除成功');
        setSelectedMessages([]);
        setMessages(prev => prev.filter(msg => !selectedMessages.includes(msg.id)));
        loadStats();
      } else {
        toast.error(response.message || '删除失败');
      }
    } catch (error) {
      toast.error('批量删除失败');
    } finally {
      setShowBatchDeleteModal(false);
    }
  };

  const cancelBatchDeleteMessage = () => {
    setShowBatchDeleteModal(false);
  };

  // 添加敏感词
  const handleAddBannedWord = async () => {
    if (!newBannedWord.trim()) {
      toast.error('请输入敏感词');
      return;
    }
    try {
      const response = await chatBannedWordApi.addBannedWord(newBannedWord.trim());
      if (response.success) {
        toast.success('添加成功');
        setNewBannedWord('');
        loadBannedWords();
        loadStats();
      } else {
        toast.error(response.message || '添加失败');
      }
    } catch (error) {
      toast.error('添加失败');
    }
  };

  // 删除敏感词
  const handleDeleteBannedWord = async (id: number) => {
    try {
      const response = await chatBannedWordApi.deleteBannedWord(id);
      if (response.success) {
        toast.success('删除成功');
        loadBannedWords();
        loadStats();
      } else {
        toast.error(response.message || '删除失败');
      }
    } catch (error) {
      toast.error('删除失败');
    }
  };

  // 搜索用户
  const searchUserInfo = async (query: string) => {
    if (!query.trim()) return;
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(async () => {
      setSearchingUser(true);
      try {
        const response = await userApi.searchUser(query.trim());
        if (response.success && response.data) {
          setMuteForm(prev => ({
            ...prev,
            user_id: String(response.data.id),
            username: response.data.username,
            nickname: response.data.nickname || ''
          }));
        }
      } catch (error) {
        console.error('搜索用户失败:', error);
      } finally {
        setSearchingUser(false);
      }
    }, 500);
  };

  // 处理用户ID输入变化
  const handleUserIdChange = (value: string) => {
    setMuteForm(prev => ({ ...prev, user_id: value }));
    if (value) {
      searchUserInfo(value);
    }
  };

  // 处理用户名输入变化
  const handleUsernameChange = (value: string) => {
    setMuteForm(prev => ({ ...prev, username: value }));
    if (value) {
      searchUserInfo(value);
    }
  };

  // 禁言用户
  const handleMuteUser = async () => {
    const userId = parseInt(muteForm.user_id);
    if (!userId || !muteForm.username) {
      toast.error('请输入用户信息');
      return;
    }
    try {
      const response = await chatMuteApi.muteUser({
        user_id: userId,
        username: muteForm.username,
        reason: muteForm.reason,
        mute_type: muteForm.mute_type,
        duration_minutes: muteForm.mute_type === 'temp' ? muteForm.duration_minutes : undefined
      });
      if (response.success) {
        toast.success(response.message);
        setShowMuteModal(false);
        setMuteForm({ user_id: '', username: '', nickname: '', reason: '', mute_type: 'permanent', duration_minutes: 30 });
        loadMutedUsers();
        loadStats();
      } else {
        toast.error(response.message || '禁言失败');
      }
    } catch (error) {
      toast.error('禁言失败');
    }
  };

  // 解除禁言
  const handleUnmuteUser = async (userId: number) => {
    try {
      const response = await chatMuteApi.unmuteUser(userId);
      if (response.success) {
        toast.success('解除禁言成功');
        loadMutedUsers();
        loadStats();
      } else {
        toast.error(response.message || '解除禁言失败');
      }
    } catch (error) {
      toast.error('解除禁言失败');
    }
  };

  const tabs = [
    { id: 'messages', label: '消息管理', icon: MessageSquare },
    { id: 'banned-words', label: '敏感词', icon: Shield },
    { id: 'mutes', label: '禁言管理', icon: Ban }
  ];

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-slate-900 mb-6">聊天室管理</h2>

      {/* 统计卡片 */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-pink-100 rounded-lg">
              <MessageSquare className="w-6 h-6 text-pink-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">消息总数</p>
              <p className="text-2xl font-bold text-slate-900">{stats.totalMessages}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">在线用户</p>
              <p className="text-2xl font-bold text-slate-900">{stats.onlineUsers}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-red-100 rounded-lg">
              <Ban className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">禁言用户</p>
              <p className="text-2xl font-bold text-slate-900">{stats.mutedUsers}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Shield className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">敏感词数</p>
              <p className="text-2xl font-bold text-slate-900">{stats.bannedWords}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 标签页 */}
      <div className="flex space-x-4 mb-6 border-b border-slate-200">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id as any);
                if (tab.id === 'banned-words') loadBannedWords();
                if (tab.id === 'mutes') loadMutedUsers();
              }}
              className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-pink-600 text-pink-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* 消息管理 */}
      {activeTab === 'messages' && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-4 border-b border-slate-200 flex justify-between items-center">
            <h3 className="font-semibold text-slate-900">聊天消息</h3>
            {selectedMessages.length > 0 && (
              <button
                onClick={handleBatchDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>批量删除 ({selectedMessages.length})</span>
              </button>
            )}
          </div>
          <div className="divide-y divide-slate-100">
            {messages.map((message) => (
              <div key={message.id} className="p-4 flex items-start space-x-4 hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={selectedMessages.includes(message.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedMessages([...selectedMessages, message.id]);
                    } else {
                      setSelectedMessages(selectedMessages.filter((id) => id !== message.id));
                    }
                  }}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-medium text-slate-900">{message.nickname || message.username}</span>
                    <span className="text-xs text-slate-400">@{message.username}</span>
                    <span className="text-xs text-slate-400">
                      {new Date(message.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-slate-700">{message.content}</p>
                  <p className="text-xs text-slate-400 mt-1">IP: {message.ip_address}</p>
                </div>
                <button
                  onClick={() => handleDeleteMessage(message.id)}
                  className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          {messages.length === 0 && (
            <div className="p-8 text-center text-slate-500">暂无消息</div>
          )}
          {/* 分页 */}
          <div className="p-4 border-t border-slate-200 flex justify-between items-center">
            <span className="text-sm text-slate-500">共 {messageTotal} 条</span>
            <div className="flex space-x-2">
              <button
                onClick={() => setMessagePage(Math.max(1, messagePage - 1))}
                disabled={messagePage === 1}
                className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50"
              >
                上一页
              </button>
              <span className="px-3 py-1">{messagePage}</span>
              <button
                onClick={() => setMessagePage(messagePage + 1)}
                disabled={messagePage * 50 >= messageTotal}
                className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 敏感词管理 */}
      {activeTab === 'banned-words' && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-4">聊天敏感词</h3>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newBannedWord}
                onChange={(e) => setNewBannedWord(e.target.value)}
                placeholder="输入敏感词"
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                onKeyPress={(e) => e.key === 'Enter' && handleAddBannedWord()}
              />
              <button
                onClick={handleAddBannedWord}
                className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>添加</span>
              </button>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {bannedWords.map((word) => (
              <div key={word.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                <div>
                  <span className="font-medium text-slate-900">{word.word}</span>
                  <span className="text-xs text-slate-400 ml-4">
                    {new Date(word.created_at).toLocaleString()}
                  </span>
                </div>
                <button
                  onClick={() => handleDeleteBannedWord(word.id)}
                  className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          {bannedWords.length === 0 && (
            <div className="p-8 text-center text-slate-500">暂无敏感词</div>
          )}
        </div>
      )}

      {/* 禁言管理 */}
      {activeTab === 'mutes' && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-4 border-b border-slate-200 flex justify-between items-center">
            <h3 className="font-semibold text-slate-900">禁言用户</h3>
            <button
              onClick={() => setShowMuteModal(true)}
              className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 flex items-center space-x-2"
            >
              <Ban className="w-4 h-4" />
              <span>添加禁言</span>
            </button>
          </div>
          <div className="divide-y divide-slate-100">
            {mutedUsers.map((mute) => (
              <div key={mute.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-slate-900">{mute.username}</span>
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      mute.mute_type === 'permanent' ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'
                    }`}>
                      {mute.mute_type === 'permanent' ? '永久' : '临时'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{mute.reason || '无原因'}</p>
                  <p className="text-xs text-slate-400">
                    禁言时间: {new Date(mute.created_at).toLocaleString()}
                    {mute.expired_at && ` - ${new Date(mute.expired_at).toLocaleString()}`}
                  </p>
                </div>
                <button
                  onClick={() => handleUnmuteUser(mute.user_id)}
                  className="p-2 hover:bg-green-50 rounded-lg text-green-600"
                >
                  <Unlock className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          {mutedUsers.length === 0 && (
            <div className="p-8 text-center text-slate-500">暂无禁言用户</div>
          )}
        </div>
      )}

      {/* 禁言弹窗 */}
      {showMuteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">禁言用户</h3>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700 mb-1">用户ID</label>
                  <input
                    type="number"
                    value={muteForm.user_id || ''}
                    onChange={(e) => handleUserIdChange(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="输入用户ID"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700 mb-1">邮箱</label>
                  <input
                    type="email"
                    value={muteForm.username}
                    onChange={(e) => handleUsernameChange(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="输入用户邮箱"
                  />
                </div>
              </div>
              {muteForm.nickname && (
                <div className="flex items-center space-x-2 text-sm text-slate-600 bg-pink-50 p-2 rounded-lg">
                  <User className="w-4 h-4 text-pink-500" />
                  <span>昵称: {muteForm.nickname}</span>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">禁言原因</label>
                <input
                  type="text"
                  value={muteForm.reason}
                  onChange={(e) => setMuteForm({ ...muteForm, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">禁言类型</label>
                <select
                  value={muteForm.mute_type}
                  onChange={(e) => setMuteForm({ ...muteForm, mute_type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="permanent">永久禁言</option>
                  <option value="temp">临时禁言</option>
                </select>
              </div>
              {muteForm.mute_type === 'temp' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">禁言时长（分钟）</label>
                  <input
                    type="number"
                    value={muteForm.duration_minutes}
                    onChange={(e) => setMuteForm({ ...muteForm, duration_minutes: parseInt(e.target.value) || 30 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>
              )}
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleMuteUser}
                className="flex-1 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700"
              >
                确定禁言
              </button>
              <button
                onClick={() => setShowMuteModal(false)}
                className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                取消
              )</button>
            </div>
          </div>
        </div>
      )}

      {/* 删除消息确认弹窗 */}
      {messageToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-96 shadow-lg">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">确认删除</h3>
            <p className="text-slate-600 mb-6">确定要删除这条消息吗？此操作不可恢复！</p>
            <div className="flex items-center justify-end space-x-4">
              <button
                onClick={cancelDeleteMessage}
                className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
              >
                取消
              </button>
              <button
                onClick={confirmDeleteMessage}
                className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 批量删除消息确认弹窗 */}
      {showBatchDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-96 shadow-lg">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">确认批量删除</h3>
            <p className="text-slate-600 mb-6">确定要删除选中的 {selectedMessages.length} 条消息吗？此操作不可恢复！</p>
            <div className="flex items-center justify-end space-x-4">
              <button
                onClick={cancelBatchDeleteMessage}
                className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
              >
                取消
              </button>
              <button
                onClick={confirmBatchDeleteMessage}
                className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatAdminPanel;
